"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useMemo, useState } from "react"
import { useTheme } from "next-themes"
import { useAuth } from "@/contexts/auth-context"
import { useRunContext } from "@/contexts/run-context"
import { $api } from "@/lib/api/client"
import { isRunActive, getRunDisplayName } from "@/lib/run-utils"
import {
  Briefcase,
  Users,
  Settings,
  Plus,
  ChevronLeft,
  ChevronRight,
  Database,
  Code2,
  Sun,
  Moon,
  LogOut,
} from "lucide-react"
import { NeuralCubeLogo } from "@/components/neural-cube-logo"
import { useTenantBranding } from "@/hooks/useTenantBranding"
import { cn } from "@/lib/utils"

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  disabled?: boolean
}

const mainNavItems: NavItem[] = [
  { label: "Workflows", href: "/workflows", icon: Briefcase },
]

const systemNavItems: NavItem[] = [
  { label: "Users", href: "/users", icon: Users },
  { label: "Settings", href: "/settings", icon: Settings },
]

/**
 * AppSidebar — Matches the Figma WIP design.
 *
 * Dark navy sidebar with MAIN and SYSTEM nav sections, a teal "Start New Run"
 * button, running jobs section, and a user profile area at the bottom.
 */
export function AppSidebar() {
  const pathname = usePathname()
  const router = useRouter()
  const { user, logout } = useAuth()
  const { data: branding } = useTenantBranding()
  const { sidebarExpanded, toggleSidebar, setRunId, navigateToRun } = useRunContext()

  const isAdmin = user?.role === "super_admin" || user?.role === "account_admin"
  const visibleSystemNav = systemNavItems.map((item) =>
    item.href === "/users" ? { ...item, disabled: !isAdmin } : item,
  )

  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  // Fetch runs with conditional polling (stop when collapsed)
  const { data: runsData } = $api.useQuery("get", "/api/workflows/", {}, {
    refetchInterval: sidebarExpanded ? 10_000 : false,
    staleTime: 5_000,
    refetchOnWindowFocus: false,
  })

  const runningJobs = useMemo(() => {
    const runs = runsData?.runs || []
    return runs.filter((run: any) => isRunActive(run)).slice(0, 5)
  }, [runsData])

  // Active state: exact match for root, startsWith for other paths.
  // Workflow detail pages (/workflow/...) intentionally match no nav item.
  const isActive = (href: string) => {
    if (href === "/") return pathname === "/"
    return pathname.startsWith(href)
  }

  const handleNewRun = () => {
    setRunId(null)
    router.push("/")
  }

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.href)
    const Icon = item.icon

    if (item.disabled) {
      return (
        <div
          key={item.label}
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-md text-sm",
            "text-sidebar-foreground/30 cursor-not-allowed",
            !sidebarExpanded && "justify-center px-2"
          )}
          title={!sidebarExpanded ? item.label : undefined}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {sidebarExpanded && <span>{item.label}</span>}
        </div>
      )
    }

    // Workflows link renders as a filled button matching "New Workflow" shape.
    // Active state adds a ring so the filled blue still reads as "current page".
    if (item.href === "/workflows") {
      return (
        <Link
          key={item.label}
          href={item.href}
          aria-current={active ? "page" : undefined}
          className={cn(
            "w-full flex items-center gap-2 rounded-md bg-[#009EDB] hover:bg-[#0BB0EE] text-white text-sm font-medium py-2.5 transition-colors shadow-md shadow-black/20",
            sidebarExpanded ? "justify-start pl-4 pr-3" : "justify-center px-2",
            active && "ring-2 ring-white/40"
          )}
          title={!sidebarExpanded ? item.label : undefined}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {sidebarExpanded && <span>{item.label}</span>}
        </Link>
      )
    }

    return (
      <Link
        key={item.label}
        href={item.href}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
          active
            ? "bg-sidebar-accent text-sidebar-foreground font-medium border-l-2 border-sidebar-primary -ml-[2px]"
            : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent",
          !sidebarExpanded && "justify-center px-2"
        )}
        title={!sidebarExpanded ? item.label : undefined}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {sidebarExpanded && <span>{item.label}</span>}
      </Link>
    )
  }

  return (
    <div
      className={cn(
        "h-screen flex flex-col bg-sidebar border-r border-sidebar-border transition-all duration-200",
        sidebarExpanded ? "w-52" : "w-14"
      )}
    >
      {/* Logo + collapse */}
      <div className="flex items-center justify-between px-3 pt-4 pb-2">
        <Link href="/" className="flex items-center gap-2">
          {branding?.logoDataUrl ? (
            <img src={branding.logoDataUrl} alt="" className="h-8 w-8 object-contain" />
          ) : (
            <NeuralCubeLogo className="h-7 w-7 shrink-0" />
          )}
          {sidebarExpanded && (
            <span
              className="text-sm font-semibold text-sidebar-foreground whitespace-nowrap"
              style={{ color: branding?.brandColor }}
            >
              {branding?.orgName || "Identity Matrix"}
            </span>
          )}
        </Link>
        {sidebarExpanded && (
          <button
            onClick={toggleSidebar}
            className="text-sidebar-foreground/40 hover:text-sidebar-foreground p-1"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Expand button (when collapsed) — anchored at top, directly below logo */}
      {!sidebarExpanded && (
        <div className="px-2 pt-1">
          <button
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center p-2 rounded-md text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* MAIN section */}
      <div className="px-2 mt-4">
        {sidebarExpanded && (
          <span className="px-3 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
            Main
          </span>
        )}
        <nav className="mt-1.5 flex flex-col gap-0.5" aria-label="Main navigation">
          {mainNavItems.map(renderNavItem)}
        </nav>
      </div>

      {/* Start New Run button */}
      <div className="px-2 mt-3">
        <button
          onClick={handleNewRun}
          className={cn(
            "w-full flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium py-2.5 transition-colors shadow-md shadow-black/20",
            sidebarExpanded ? "justify-start pl-4 pr-3" : "justify-center px-2"
          )}
        >
          <Plus className="h-4 w-4 shrink-0" />
          {sidebarExpanded && <span>New Workflow</span>}
        </button>
      </div>

      {/* RUNNING section */}
      {sidebarExpanded && runningJobs.length > 0 && (
        <div className="px-2 mt-4">
          <span className="px-3 text-[10px] font-semibold uppercase tracking-wider text-sidebar-primary/70">
            Running
          </span>
          <div className="mt-1.5 flex flex-col gap-1">
            {runningJobs.map((run: any) => {
              const isCurrentRun = pathname === `/workflow/${run.run_id}`
              return (
              <button
                key={run.run_id}
                onClick={() => navigateToRun(run.run_id, run.mode)}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors text-left w-full",
                  isCurrentRun
                    ? "bg-sidebar-accent text-sidebar-foreground font-medium"
                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent"
                )}
              >
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                </span>
                {run.mode === "database" ? (
                  <Database className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/40" />
                ) : (
                  <Code2 className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/40" />
                )}
                <span className="truncate text-xs">{getRunDisplayName(run)}</span>
              </button>
              )
            })}
          </div>
        </div>
      )}

      {/* SYSTEM section */}
      <div className="px-2 mt-6">
        {sidebarExpanded && (
          <span className="px-3 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
            System
          </span>
        )}
        <nav className="mt-1.5 flex flex-col gap-0.5" aria-label="System navigation">
          {visibleSystemNav.map(renderNavItem)}
        </nav>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Theme toggle */}
      <div className={cn("px-2", sidebarExpanded ? "pb-1" : "pb-2")}>
        <button
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className={cn(
            "w-full flex items-center gap-2 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors",
            sidebarExpanded ? "px-3 py-2" : "justify-center p-2"
          )}
          title={sidebarExpanded ? undefined : (mounted && theme === "dark" ? "Light mode" : "Dark mode")}
        >
          {mounted && theme === "dark" ? (
            <Sun className="h-4 w-4 shrink-0" />
          ) : (
            <Moon className="h-4 w-4 shrink-0" />
          )}
          {sidebarExpanded && (
            <span className="text-xs">{mounted && theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
          )}
        </button>
      </div>

      {/* User profile + logout */}
      {sidebarExpanded && user && (
        <div className="border-t border-sidebar-border p-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-sidebar-primary/20 flex items-center justify-center text-xs font-medium text-sidebar-foreground">
              {user.email[0].toUpperCase()}
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-xs font-medium text-sidebar-foreground truncate">{user.email}</span>
              <span className="text-[10px] text-sidebar-foreground/50 capitalize">{user.role.replace("_", " ")}</span>
            </div>
            <button
              onClick={logout}
              className="p-1 rounded text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
